import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { onAuthStateChanged, type User } from "firebase/auth";
import { getDoc, doc } from "firebase/firestore";
import { auth, db } from "../services/firebase";
import createUserIfNotExists from "../features/users/helpers/createUserIfNotExists";
import { getE2EAuthState, isE2EMockAuthEnabled } from "../utils/e2eAuth";

const createUserIfNotExistsTyped = createUserIfNotExists as (
  uid: string,
  email: string | null
) => Promise<void>;

type AppUserData = {
  role?: string;
  active?: boolean;
  actif?: boolean;
  deleted?: boolean;
  archived?: boolean;
};

type UseAppUserResult = {
  firebaseUser: User | null;
  uid: string | null;
  email: string | null;
  displayName: string | null;
  role: string | null;
  actif: boolean;
  loading: boolean;
};

const useAppUser = (): UseAppUserResult => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [e2eAppUserData, setE2EAppUserData] = useState<AppUserData | null>(null);
  const [authLoading, setAuthLoading] = useState<boolean>(true);

  const appUserQuery = useQuery<AppUserData | null>({
    queryKey: ["app-user", firebaseUser?.uid ?? null],
    enabled: Boolean(firebaseUser?.uid) && !isE2EMockAuthEnabled(),
    queryFn: async () => {
      if (!firebaseUser) return null;

      await createUserIfNotExistsTyped(firebaseUser.uid, firebaseUser.email ?? null);

      const userDocRef = doc(db, "users", firebaseUser.uid);
      const userSnap = await getDoc(userDocRef);

      if (userSnap.exists()) {
        return (userSnap.data() as AppUserData) ?? null;
      }

      return null;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    if (isE2EMockAuthEnabled()) {
      const applyE2EState = () => {
        const e2eState = getE2EAuthState();
        if (e2eState) {
          setFirebaseUser({
            uid: e2eState.uid,
            email: e2eState.email,
            displayName: e2eState.displayName,
          } as User);
          setE2EAppUserData({ role: e2eState.role, actif: true });
        } else {
          setFirebaseUser(null);
          setE2EAppUserData(null);
        }
        setAuthLoading(false);
      };

      applyE2EState();
      window.addEventListener("storage", applyE2EState);
      window.addEventListener("e2e-auth-changed", applyE2EState);

      return () => {
        window.removeEventListener("storage", applyE2EState);
        window.removeEventListener("e2e-auth-changed", applyE2EState);
      };
    }

    const loadingFallback = window.setTimeout(() => {
      setAuthLoading(false);
    }, 2500);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      window.clearTimeout(loadingFallback);
      if (user) {
        setFirebaseUser(user);
      } else {
        setFirebaseUser(null);
        setE2EAppUserData(null);
      }

      setAuthLoading(false);
    });

    return () => {
      window.clearTimeout(loadingFallback);
      unsubscribe();
    };
  }, []);

  const appUserData = isE2EMockAuthEnabled() ? e2eAppUserData : (appUserQuery.data ?? null);
  
  // A user is fully loaded when:
  // 1. We are not checking auth state AND
  // 2. If we have a firebase user, we must also have finished fetching their appUserQuery
  const isQueryActive = Boolean(firebaseUser) && !isE2EMockAuthEnabled() && (appUserQuery.isFetching || appUserQuery.isPending);
  // Fallback: If we have a user, but no role yet, and we are still fetching, we should consider it loading
  const loading = authLoading || isQueryActive;

  return {
    firebaseUser,
    uid: firebaseUser?.uid ?? null,
    email: firebaseUser?.email ?? null,
    displayName: firebaseUser?.displayName ?? null,
    role: appUserData?.role ?? null,
    actif: appUserData?.deleted === true || appUserData?.archived === true
      ? false
      : appUserData?.active === false
        ? false
        : appUserData?.actif !== false,
    loading,
  };
};

export default useAppUser;
